import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";

// components
import AnimationLoading from "../../../components/Animationloading";
import CustomButton from "../../../components/CustomButton";
import EmptyComponent from "../../../components/EmptyComponent";
import ConfirmDelete from "../../../components/ConfirmDelete";
import { LINEAR_GRADIENT_BG, USER_KEY } from "../../../const";
import {
  errorSwal,
  successDeleteSwal,
  successSwal,
  successUpdateSwal,
} from "../../../helper/sweetalert";

// apollo
import { useQuery, useLazyQuery, useMutation } from "@apollo/client";
import { GET_PRODUCTS } from "../../../apollo/product/query";
import { GET_DAMAGED_PRODUCTS } from "../../../apollo/damage/Query";
import {
  CREATE_DAMAGED_PRODUCT,
  UPDATE_DAMAGED_PRODUCT,
  DELETE_DAMAGED_PRODUCT,
} from "../../../apollo/damage/Mutation";

// icons
import SearchIcon from "@mui/icons-material/Search";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdOutlineBorderColor } from "react-icons/md";
import { BsDatabaseFillAdd } from "react-icons/bs";
import ReactPaginate from "react-paginate";

// bootstrap
import { Form, Modal, Table, InputGroup } from "react-bootstrap";

const LIMIT_PAGE = 50;

const DAMAGE_TYPES = [
  { value: "BROKEN", label: "ແຕກ/ຫັກ" },
  { value: "EXPIRED", label: "ໝົດອາຍຸ" },
  { value: "LOST", label: "ສູນຫາຍ" },
  { value: "OTHER", label: "ອື່ນໆ" },
];

export default function DamagedInventory() {
  const shopId = JSON.parse(localStorage.getItem(USER_KEY))?.data?.shop?.id;

  const [activeTab, setActiveTab] = useState("PRODUCT");

  // Product tab state
  const [productSearchText, setProductSearchText] = useState("");
  const [pageNumber, setPageNumber] = useState(1);

  // Damaged list tab state
  const [damagedSearchText, setDamagedSearchText] = useState("");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Multi-select state
  const [selectedRows, setSelectedRows] = useState({}); // { [productId]: product }
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkItems, setBulkItems] = useState([]); // [{ product, damageType, damageReason, damagedAmount, damageDate }]
  const [bulkSaving, setBulkSaving] = useState(false);

  // Delete state
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState("");

  const initialForm = {
    id: "",
    damageType: "",
    damageReason: "",
    damagedAmount: 1,
    damageDate: moment().format("YYYY-MM-DD"),
  };
  const [formData, setFormData] = useState(initialForm);

  // Apollo
  const [loadProducts, { data: productsData, loading: productsLoading }] =
    useLazyQuery(GET_PRODUCTS, { fetchPolicy: "cache-and-network" });

  const {
    data: damagedData,
    loading: damagedLoading,
    refetch: refetchDamaged,
  } = useQuery(GET_DAMAGED_PRODUCTS, { fetchPolicy: "cache-and-network" });

  const [createDamagedProduct, { loading: creating }] = useMutation(
    CREATE_DAMAGED_PRODUCT
  );
  const [updateDamagedProduct, { loading: updating }] = useMutation(
    UPDATE_DAMAGED_PRODUCT
  );
  const [deleteDamagedProduct, { loading: deleting }] = useMutation(
    DELETE_DAMAGED_PRODUCT
  );

  // Load products whenever we're on the product tab / search / page changes
  useEffect(() => {
    if (activeTab !== "PRODUCT") return;
    const where = { shop: shopId, isDeleted: false };
    if (productSearchText) where.name = productSearchText;
    loadProducts({
      variables: {
        skip: (pageNumber - 1) * LIMIT_PAGE,
        limit: LIMIT_PAGE,
        orderBy: "createdAt_DESC",
        where,
      },
    });
  }, [activeTab, productSearchText, pageNumber, shopId, loadProducts]);

  // Reset to first page whenever search changes
  useEffect(() => {
    setPageNumber(1);
  }, [productSearchText]);

  const products = productsData?.products?.data || [];
  const totalProduct = productsData?.products?.total || 0;

  const damagedProducts = useMemo(
    () => (damagedData?.damagedProducts || []).filter((item) => !item?.isDeleted),
    [damagedData]
  );

  const filteredDamaged = useMemo(() => {
    if (!damagedSearchText) return damagedProducts;
    const q = damagedSearchText.toLowerCase();
    return damagedProducts.filter(
      (item) =>
        (item?.name || "").toLowerCase().includes(q) ||
        (item?.damageType || "").toLowerCase().includes(q) ||
        (item?.damageReason || "").toLowerCase().includes(q)
    );
  }, [damagedProducts, damagedSearchText]);

  const getDamageTypeLabel = (value) =>
    DAMAGE_TYPES.find((t) => t.value === value)?.label || value || "-";

  const openCreateFromProduct = (product) => {
    setSelectedProduct(product);
    setFormData(initialForm);
    setIsEdit(false);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setFormData({
      id: item?.id,
      damageType: item?.damageType || "",
      damageReason: item?.damageReason || "",
      damagedAmount: item?.damagedAmount || 0,
      damageDate: item?.damageDate
        ? moment(item.damageDate).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD"),
    });
    setSelectedProduct({
      id: item?.stock?.id,
      name: item?.name,
      amount: null,
    });
    setIsEdit(true);
    setShowModal(true);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Multi-select helpers
  const isRowSelected = (product) => !!selectedRows[product?.id];

  const toggleRow = (product) => {
    setSelectedRows((prev) => {
      const next = { ...prev };
      if (next[product?.id]) delete next[product.id];
      else next[product.id] = product;
      return next;
    });
  };

  const selectedCount = Object.keys(selectedRows).length;
  const allPageSelected =
    products.length > 0 && products.every((p) => isRowSelected(p));

  const toggleSelectAll = () => {
    if (allPageSelected) {
      // Deselect everything on current page
      setSelectedRows((prev) => {
        const next = { ...prev };
        products.forEach((p) => delete next[p.id]);
        return next;
      });
    } else {
      setSelectedRows((prev) => {
        const next = { ...prev };
        products.forEach((p) => {
          if (p?.amount > 0) next[p.id] = p;
        });
        return next;
      });
    }
  };

  const clearSelection = () => setSelectedRows({});

  const openBulkModal = () => {
    const items = Object.values(selectedRows).map((product) => ({
      product,
      damageType: "",
      damageReason: "",
      damagedAmount: 1,
      damageDate: moment().format("YYYY-MM-DD"),
    }));
    setBulkItems(items);
    setShowBulkModal(true);
  };

  const updateBulkItem = (index, field, value) => {
    setBulkItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const removeBulkItem = (index) => {
    setBulkItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBulkSave = async () => {
    try {
      if (bulkItems.length === 0) {
        return errorSwal("ແຈ້ງເຕືອນ", "ບໍ່ມີລາຍການໃຫ້ບັນທຶກ");
      }
      // Validate
      for (const item of bulkItems) {
        if (!item.damagedAmount || Number(item.damagedAmount) <= 0) {
          return errorSwal(
            "ແຈ້ງເຕືອນ",
            `ກະລຸນາປ້ອນຈຳນວນທີ່ເສຍຫາຍໃຫ້ "${item.product?.name}"`
          );
        }
        if (
          item.product?.amount != null &&
          Number(item.damagedAmount) > Number(item.product.amount)
        ) {
          return errorSwal(
            "ແຈ້ງເຕືອນ",
            `ຈຳນວນເສຍຫາຍຂອງ "${item.product?.name}" ເກີນສະຕ໋ອກ (${item.product.amount})`
          );
        }
      }

      setBulkSaving(true);
      for (const item of bulkItems) {
        await createDamagedProduct({
          variables: {
            data: {
              name: item.product?.name,
              stock: item.product?.id,
              shop: shopId,
              damageType: item.damageType,
              damageReason: item.damageReason,
              damagedAmount: parseFloat(item.damagedAmount) || 0,
              damageDate: item.damageDate,
            },
          },
        });
      }

      successSwal("ສຳເລັດ", `ບັນທຶກ ${bulkItems.length} ລາຍການສຳເລັດ`, "success");
      setShowBulkModal(false);
      setBulkItems([]);
      clearSelection();
      await refetchDamaged();
      loadProducts({
        variables: {
          skip: (pageNumber - 1) * LIMIT_PAGE,
          limit: LIMIT_PAGE,
          orderBy: "createdAt_DESC",
          where: productSearchText
            ? { shop: shopId, isDeleted: false, name: productSearchText }
            : { shop: shopId, isDeleted: false },
        },
      });
    } catch (error) {
      console.error("Bulk save failed:", error);
      errorSwal("ຜິດພາດ", "ບັນທຶກບໍ່ສຳເລັດ");
    } finally {
      setBulkSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      if (!selectedProduct) {
        return errorSwal("ແຈ້ງເຕືອນ", "ກະລຸນາເລືອກສິນຄ້າ");
      }
      if (!formData.damagedAmount || Number(formData.damagedAmount) <= 0) {
        return errorSwal("ແຈ້ງເຕືອນ", "ກະລຸນາປ້ອນຈຳນວນທີ່ເສຍຫາຍ");
      }
      if (
        !isEdit &&
        selectedProduct?.amount != null &&
        Number(formData.damagedAmount) > Number(selectedProduct.amount)
      ) {
        return errorSwal(
          "ແຈ້ງເຕືອນ",
          `ຈຳນວນເສຍຫາຍເກີນສະຕ໋ອກທີ່ມີ (${selectedProduct.amount})`
        );
      }

      const payload = {
        name: selectedProduct?.name,
        stock: selectedProduct?.id,
        shop: shopId,
        damageType: formData.damageType,
        damageReason: formData.damageReason,
        damagedAmount: parseFloat(formData.damagedAmount) || 0,
        damageDate: formData.damageDate,
      };

      if (isEdit) {
        await updateDamagedProduct({
          variables: { where: { id: formData.id }, data: payload },
        });
        successUpdateSwal();
      } else {
        await createDamagedProduct({ variables: { data: payload } });
        successSwal("ສຳເລັດ", "ເພີ່ມລາຍການເສຍຫາຍສຳເລັດ", "success");
      }

      setShowModal(false);
      setFormData(initialForm);
      setSelectedProduct(null);
      await refetchDamaged();
      // Also refresh the product list so amounts update
      if (activeTab === "PRODUCT") {
        const where = { shop: shopId, isDeleted: false };
        if (productSearchText) where.name = productSearchText;
        loadProducts({
          variables: {
            skip: (pageNumber - 1) * LIMIT_PAGE,
            limit: LIMIT_PAGE,
            orderBy: "createdAt_DESC",
            where,
          },
        });
      }
    } catch (error) {
      console.error("Save damaged product failed:", error);
      errorSwal("ຜິດພາດ", "ບັນທຶກບໍ່ສຳເລັດ");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDamagedProduct({ variables: { where: { id: deleteId } } });
      successDeleteSwal();
      setShowDelete(false);
      setDeleteId("");
      await refetchDamaged();
    } catch (error) {
      console.error("Delete damaged product failed:", error);
      errorSwal("ຜິດພາດ", "ລຶບບໍ່ສຳເລັດ");
    }
  };

  const tabButtonStyle = (isActive) => ({
    height: 40,
    padding: "0 24px",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
    fontWeight: 500,
    color: isActive ? "#FFF" : "#4a5568",
    background: isActive ? LINEAR_GRADIENT_BG : "#E5E7EB",
  });

  return (
    <React.Fragment>
      <div style={{ padding: "20px" }}>
        {/* Tabs */}
        <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
          <button
            style={tabButtonStyle(activeTab === "PRODUCT")}
            onClick={() => setActiveTab("PRODUCT")}
          >
            ເລືອກສິນຄ້າເພີ່ມເສຍຫາຍ
          </button>
          <button
            style={tabButtonStyle(activeTab === "DAMAGED")}
            onClick={() => setActiveTab("DAMAGED")}
          >
            ລາຍການເສຍຫາຍ
          </button>
        </div>

        {activeTab === "PRODUCT" ? (
          <>
            <div
              style={{ display: "flex", gap: 10, marginBottom: 15 }}
            >
              <InputGroup style={{ flex: 1 }}>
                <InputGroup.Text style={{ backgroundColor: "#FFFFFF" }}>
                  <SearchIcon sx={{ color: "#6B7280" }} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="ຄົ້ນຫາສິນຄ້າ..."
                  value={productSearchText}
                  onChange={(e) => setProductSearchText(e.target.value)}
                  style={{ borderLeft: "none" }}
                />
              </InputGroup>
              {selectedCount > 0 && (
                <>
                  <CustomButton
                    height={40}
                    width={220}
                    borderRadius={8}
                    title={`ເພີ່ມເສຍຫາຍ (${selectedCount})`}
                    icon={<BsDatabaseFillAdd />}
                    background={LINEAR_GRADIENT_BG}
                    color="#FFF"
                    onClick={openBulkModal}
                  />
                  <CustomButton
                    height={40}
                    width={120}
                    borderRadius={8}
                    title="ລ້າງ"
                    background="#6B7280"
                    color="#FFF"
                    onClick={clearSelection}
                  />
                </>
              )}
            </div>

            <Table hover style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead style={{ background: LINEAR_GRADIENT_BG, color: "#FFFFFF" }}>
                <tr>
                  <th style={{ padding: 10, width: 40 }}>
                    <Form.Check
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th style={{ padding: 10 }}>ລ/ດ</th>
                  <th>ຊື່ສິນຄ້າ</th>
                  <th>ບາໂຄດ</th>
                  <th>ປະເພດ</th>
                  <th>ແບຣນ</th>
                  <th>ຈຳນວນໃນສະຕ໋ອກ</th>
                  <th>ຫົວໜ່ວຍ</th>
                  <th>ຈັດການ</th>
                </tr>
              </thead>
              <tbody>
                {productsLoading && !productsData ? (
                  <tr>
                    <td colSpan={9}>
                      <AnimationLoading />
                    </td>
                  </tr>
                ) : products.length > 0 ? (
                  products.map((product, idx) => (
                    <tr
                      key={product?.id || idx}
                      style={{
                        backgroundColor: isRowSelected(product)
                          ? "#EEF2FF"
                          : "transparent",
                      }}
                    >
                      <td style={{ paddingTop: "1rem" }}>
                        <Form.Check
                          type="checkbox"
                          checked={isRowSelected(product)}
                          disabled={product?.amount <= 0}
                          onChange={() => toggleRow(product)}
                        />
                      </td>
                      <td style={{ paddingTop: "1rem" }}>
                        {idx + 1 + LIMIT_PAGE * (pageNumber - 1)}
                      </td>
                      <td style={{ paddingTop: "1rem" }}>
                        {product?.name || "-"}
                      </td>
                      <td style={{ paddingTop: "1rem" }}>
                        {product?.barCode || "-"}
                      </td>
                      <td style={{ paddingTop: "1rem" }}>
                        {product?.category?.name ?? product?.categoryName ?? "-"}
                      </td>
                      <td style={{ paddingTop: "1rem" }}>
                        {product?.brand?.name ?? product?.brandName ?? "-"}
                      </td>
                      <td
                        style={{
                          paddingTop: "1rem",
                          color: product?.amount <= 0 ? "red" : "",
                          fontWeight: product?.amount <= 0 ? 600 : 400,
                        }}
                      >
                        {product?.amount ?? 0}
                      </td>
                      <td style={{ paddingTop: "1rem" }}>
                        {product?.unitId?.name || product?.unit || "-"}
                      </td>
                      <td style={{ paddingTop: "0.6rem" }}>
                        <CustomButton
                          height={32}
                          width={120}
                          borderRadius={6}
                          title="ເພີ່ມເສຍຫາຍ"
                          icon={<BsDatabaseFillAdd />}
                          background={LINEAR_GRADIENT_BG}
                          color="#FFF"
                          disabled={product?.amount <= 0}
                          onClick={() => openCreateFromProduct(product)}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          padding: "1.5rem 0",
                          color: "#6B7280",
                        }}
                      >
                        <EmptyComponent />
                        <p>ບໍ່ພົບສິນຄ້າ</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {totalProduct > LIMIT_PAGE && (
              <div style={{ marginTop: 20 }}>
                <ReactPaginate
                  previousLabel="ກັບຄືນ"
                  nextLabel="ຕໍ່ໄປ"
                  breakLabel="..."
                  onPageChange={(e) => setPageNumber(e.selected + 1)}
                  pageRangeDisplayed={2}
                  marginPagesDisplayed={2}
                  pageCount={Math.ceil(totalProduct / LIMIT_PAGE)}
                  forcePage={pageNumber - 1}
                  renderOnZeroPageCount={null}
                  containerClassName="pagination justify-content-center"
                  pageClassName="page-item"
                  pageLinkClassName="page-link"
                  previousClassName="page-item"
                  previousLinkClassName="page-link"
                  nextClassName="page-item"
                  nextLinkClassName="page-link"
                  breakClassName="page-item"
                  breakLinkClassName="page-link"
                  activeClassName="active"
                />
              </div>
            )}
          </>
        ) : (
          <>
            <InputGroup style={{ marginBottom: 15 }}>
              <InputGroup.Text style={{ backgroundColor: "#FFFFFF" }}>
                <SearchIcon sx={{ color: "#6B7280" }} />
              </InputGroup.Text>
              <Form.Control
                placeholder="ຄົ້ນຫາລາຍການເສຍຫາຍ..."
                value={damagedSearchText}
                onChange={(e) => setDamagedSearchText(e.target.value)}
                style={{ borderLeft: "none" }}
              />
            </InputGroup>

            <Table hover style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead style={{ background: LINEAR_GRADIENT_BG, color: "#FFFFFF" }}>
                <tr>
                  <th style={{ padding: 10 }}>ລ/ດ</th>
                  <th>ຊື່ສິນຄ້າ</th>
                  <th>ປະເພດຄວາມເສຍຫາຍ</th>
                  <th>ເຫດຜົນ</th>
                  <th>ຈຳນວນ</th>
                  <th>ວັນທີເສຍຫາຍ</th>
                  <th>ຈັດການ</th>
                </tr>
              </thead>
              <tbody>
                {damagedLoading ? (
                  <tr>
                    <td colSpan={7}>
                      <AnimationLoading />
                    </td>
                  </tr>
                ) : filteredDamaged.length > 0 ? (
                  filteredDamaged.map((item, index) => (
                    <tr key={item?.id || index}>
                      <td style={{ paddingTop: "1rem" }}>{index + 1}</td>
                      <td style={{ paddingTop: "1rem" }}>{item?.name || "-"}</td>
                      <td style={{ paddingTop: "1rem" }}>
                        {getDamageTypeLabel(item?.damageType)}
                      </td>
                      <td style={{ paddingTop: "1rem" }}>
                        {item?.damageReason || "-"}
                      </td>
                      <td style={{ paddingTop: "1rem" }}>
                        {item?.damagedAmount ?? 0}
                      </td>
                      <td style={{ paddingTop: "1rem" }}>
                        {item?.damageDate
                          ? moment(item.damageDate).format("DD/MM/YYYY")
                          : "-"}
                      </td>
                      <td style={{ paddingTop: "0.5rem" }}>
                        <div style={{ display: "flex", gap: 10 }}>
                          <MdOutlineBorderColor
                            size={20}
                            style={{ cursor: "pointer", color: "#3C169B" }}
                            onClick={() => openEditModal(item)}
                          />
                          <RiDeleteBin6Line
                            size={20}
                            style={{ cursor: "pointer", color: "red" }}
                            onClick={() => {
                              setDeleteId(item?.id);
                              setShowDelete(true);
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          padding: "1.5rem 0",
                          color: "#6B7280",
                        }}
                      >
                        <EmptyComponent />
                        <p>ຍັງບໍ່ມີລາຍການສິນຄ້າເສຍຫາຍ</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </>
        )}
      </div>

      {/* Bulk Create Modal */}
      <Modal
        show={showBulkModal}
        onHide={() => setShowBulkModal(false)}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            ເພີ່ມລາຍການເສຍຫາຍ ({bulkItems.length})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ maxHeight: 500, overflowY: "auto" }}>
            <Table bordered style={{ fontSize: 14 }}>
              <thead
                style={{
                  background: LINEAR_GRADIENT_BG,
                  color: "#FFF",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                <tr>
                  <th style={{ padding: 8, width: 40 }}>#</th>
                  <th>ຊື່ສິນຄ້າ</th>
                  <th style={{ width: 100 }}>ສະຕ໋ອກ</th>
                  <th style={{ width: 110 }}>ຈຳນວນເສຍ</th>
                  <th style={{ width: 160 }}>ປະເພດ</th>
                  <th style={{ width: 140 }}>ວັນທີ</th>
                  <th>ເຫດຜົນ</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {bulkItems.map((item, idx) => (
                  <tr key={item.product?.id || idx}>
                    <td style={{ paddingTop: "0.8rem" }}>{idx + 1}</td>
                    <td style={{ paddingTop: "0.8rem" }}>
                      {item.product?.name || "-"}
                    </td>
                    <td style={{ paddingTop: "0.8rem" }}>
                      {item.product?.amount ?? 0}
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        min="1"
                        value={item.damagedAmount}
                        onChange={(e) =>
                          updateBulkItem(idx, "damagedAmount", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Form.Select
                        value={item.damageType}
                        onChange={(e) =>
                          updateBulkItem(idx, "damageType", e.target.value)
                        }
                      >
                        <option value="">--</option>
                        {DAMAGE_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Control
                        type="date"
                        value={item.damageDate}
                        onChange={(e) =>
                          updateBulkItem(idx, "damageDate", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={item.damageReason}
                        onChange={(e) =>
                          updateBulkItem(idx, "damageReason", e.target.value)
                        }
                        placeholder="ເຫດຜົນ"
                      />
                    </td>
                    <td style={{ textAlign: "center", paddingTop: "0.6rem" }}>
                      <RiDeleteBin6Line
                        size={20}
                        style={{ cursor: "pointer", color: "red" }}
                        onClick={() => removeBulkItem(idx)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <CustomButton
            height={36}
            width={100}
            borderRadius={6}
            title="ຍົກເລີກ"
            background="#6B7280"
            color="#FFF"
            onClick={() => setShowBulkModal(false)}
          />
          <CustomButton
            height={36}
            width={160}
            borderRadius={6}
            title={bulkSaving ? "ກຳລັງບັນທຶກ..." : `ບັນທຶກທັງໝົດ (${bulkItems.length})`}
            background={LINEAR_GRADIENT_BG}
            color="#FFF"
            onClick={handleBulkSave}
            disabled={bulkSaving || bulkItems.length === 0}
          />
        </Modal.Footer>
      </Modal>

      {/* Create / Edit Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEdit ? "ແກ້ໄຂລາຍການເສຍຫາຍ" : "ເພີ່ມລາຍການເສຍຫາຍ"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600 }}>ສິນຄ້າ</Form.Label>
              <div
                style={{
                  padding: "10px 14px",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  backgroundColor: "#f8f9fa",
                }}
              >
                <div style={{ fontWeight: 600 }}>
                  {selectedProduct?.name || "-"}
                </div>
                {selectedProduct?.amount != null && (
                  <div style={{ fontSize: 13, color: "#6B7280" }}>
                    ຈຳນວນໃນສະຕ໋ອກ: {selectedProduct?.amount}
                  </div>
                )}
              </div>
            </Form.Group>

            <div style={{ display: "flex", gap: 12 }}>
              <Form.Group className="mb-3" style={{ flex: 1 }}>
                <Form.Label>ປະເພດຄວາມເສຍຫາຍ</Form.Label>
                <Form.Select
                  value={formData.damageType}
                  onChange={(e) => handleChange("damageType", e.target.value)}
                >
                  <option value="">-- ເລືອກປະເພດ --</option>
                  {DAMAGE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" style={{ flex: 1 }}>
                <Form.Label>
                  ຈຳນວນທີ່ເສຍຫາຍ <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={formData.damagedAmount}
                  onChange={(e) =>
                    handleChange("damagedAmount", e.target.value)
                  }
                />
              </Form.Group>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>ວັນທີເສຍຫາຍ</Form.Label>
              <Form.Control
                type="date"
                value={formData.damageDate}
                onChange={(e) => handleChange("damageDate", e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>ເຫດຜົນ</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.damageReason}
                onChange={(e) => handleChange("damageReason", e.target.value)}
                placeholder="ອະທິບາຍສາເຫດ..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <CustomButton
            height={36}
            width={100}
            borderRadius={6}
            title="ຍົກເລີກ"
            background="#6B7280"
            color="#FFF"
            onClick={() => setShowModal(false)}
          />
          <CustomButton
            height={36}
            width={120}
            borderRadius={6}
            title={creating || updating ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
            background={LINEAR_GRADIENT_BG}
            color="#FFF"
            onClick={handleSave}
            disabled={creating || updating}
          />
        </Modal.Footer>
      </Modal>

      <ConfirmDelete
        open={showDelete}
        onClose={() => setShowDelete(false)}
        title="ຢືນຢັນ"
        paragrap="ທ່ານຕ້ອງການລຶບລາຍການນີ້ ຫຼື ບໍ່?"
        onSubmit={handleDelete}
      />
    </React.Fragment>
  );
}
